/**
 * Cache Migration Utilities
 * 
 * This module handles the one-time migration of cached data from integer IDs
 * to GUID identifiers during the frontend GUID migration (spec 009).
 * 
 * The clearLegacyIdCache() function should be called once on app initialization
 * to clear any localStorage/sessionStorage data containing old integer entity IDs.
 * 
 * @module cacheMigration
 */

import { looksLikeLegacyInteger } from './guidValidation';

/**
 * Known localStorage/sessionStorage keys that may contain entity IDs
 * requiring migration from integer to GUID format.
 * 
 * Add any application-specific keys that cache entity references.
 */
const KEYS_TO_CHECK = [
  'selectedClientId',
  'lastViewedLeadId',
  'cachedUserProfile',
  'recentProperties',
  'currentClient',
  'activeSubscription',
  'assignedLeads',
  'favoriteProperties',
  'recentClients',
  'selectedUser',
  'lastViewedProperty',
  'currentLead',
];

/**
 * Checks if an object contains any field with an integer ID that needs migration.
 * 
 * @param {any} obj - The object to check (can be primitive or complex)
 * @returns {boolean} True if object contains legacy integer ID
 */
function hasIntegerId(obj) {
  if (obj == null) return false;
  
  // Primitive value - check if it's an integer
  if (typeof obj !== 'object') {
    return looksLikeLegacyInteger(obj);
  }
  
  // Array - check each element
  if (Array.isArray(obj)) {
    return obj.some(item => hasIntegerId(item));
  }
  
  // Object - check fields that commonly contain entity IDs
  const idFieldNames = [
    'id', 'clientId', 'userId', 'leadId', 'propertyId', 'subscriptionId',
    'buyerId', 'userRoleId', 'assignedUserId', 'subscriptionPlanId',
    'templateId', 'campaignId', 'contentId', 'accountId'
  ];
  
  for (const key of Object.keys(obj)) {
    const lowerKey = key.toLowerCase();
    
    // Check if this is an ID field
    if (idFieldNames.some(idField => lowerKey.includes(idField.toLowerCase()))) {
      if (looksLikeLegacyInteger(obj[key])) {
        return true;
      }
    }
    
    // Recursively check nested objects
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      if (hasIntegerId(obj[key])) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Clears legacy cached data containing integer entity IDs from browser storage.
 * 
 * This function:
 * 1. Checks known cache keys for integer ID references
 * 2. Removes any cache entries containing old integer IDs
 * 3. Marks migration as complete to avoid repeated checks
 * 
 * Should be called once during app initialization (in index.js or App.js).
 * 
 * @returns {Object} Migration result with cleared keys count
 * 
 * @example
 * import { clearLegacyIdCache } from './shared/utils/cacheMigration';
 * 
 * // In app initialization:
 * if (!localStorage.getItem('guidMigrationCleared')) {
 *   const result = clearLegacyIdCache();
 *   console.log(`Cleared ${result.clearedCount} legacy cache entries`);
 * }
 */
export function clearLegacyIdCache() {
  const result = {
    clearedCount: 0,
    clearedKeys: [],
    timestamp: new Date().toISOString()
  };
  
  // Check if migration already completed
  if (localStorage.getItem('guidMigrationCleared') === 'true') {
    console.info('[CacheMigration] Cache migration already completed. Skipping.');
    return result;
  }
  
  console.info('[CacheMigration] Starting legacy cache cleanup...');
  
  // Check localStorage
  KEYS_TO_CHECK.forEach(key => {
    try {
      const value = localStorage.getItem(key);
      if (value) {
        let parsed;
        try {
          parsed = JSON.parse(value);
        } catch {
          // Not JSON, check raw value
          parsed = value;
        }
        
        if (hasIntegerId(parsed)) {
          console.warn(`[CacheMigration] Clearing legacy cache key: ${key}`);
          localStorage.removeItem(key);
          result.clearedCount++;
          result.clearedKeys.push(key);
        }
      }
    } catch (error) {
      console.error(`[CacheMigration] Error checking key "${key}":`, error);
    }
  });
  
  // Check sessionStorage
  KEYS_TO_CHECK.forEach(key => {
    try {
      const value = sessionStorage.getItem(key);
      if (value) {
        let parsed;
        try {
          parsed = JSON.parse(value);
        } catch {
          parsed = value;
        }
        
        if (hasIntegerId(parsed)) {
          console.warn(`[CacheMigration] Clearing legacy session key: ${key}`);
          sessionStorage.removeItem(key);
          result.clearedCount++;
          result.clearedKeys.push(`session:${key}`);
        }
      }
    } catch (error) {
      console.error(`[CacheMigration] Error checking session key "${key}":`, error);
    }
  });
  
  // Mark migration as complete
  localStorage.setItem('guidMigrationCleared', 'true');
  localStorage.setItem('guidMigrationTimestamp', result.timestamp);
  
  console.info(`[CacheMigration] Migration complete. Cleared ${result.clearedCount} cache entries.`);
  
  return result;
}

/**
 * Forces a re-run of cache migration (useful for testing or debugging).
 * 
 * @example
 * import { resetMigrationFlag } from './shared/utils/cacheMigration';
 * resetMigrationFlag();
 * clearLegacyIdCache(); // Will run again
 */
export function resetMigrationFlag() {
  localStorage.removeItem('guidMigrationCleared');
  localStorage.removeItem('guidMigrationTimestamp');
  console.warn('[CacheMigration] Migration flag reset. Cache cleanup will run on next app load.');
}

/**
 * Checks if cache migration has been completed.
 * 
 * @returns {Object} Migration status with completion flag and timestamp
 */
export function getMigrationStatus() {
  return {
    completed: localStorage.getItem('guidMigrationCleared') === 'true',
    timestamp: localStorage.getItem('guidMigrationTimestamp') || null
  };
}
